/**
 * PTE Simulation Page — /pte/simulation/:section
 *
 * A single full-screen Winhouse assessment shell handling all Simulation
 * Mode states: system_check → test_info → in_progress → complete → results.
 *
 * Consumes the stable Phase 1B-Alpha backend contracts at /api/pte/simulation/*.
 * Timer is display-only; server remains authoritative on remaining_seconds.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import RENDERERS from '@/components/pte/PTERenderers';
import { Loader2, Mic, Volume2, Wifi, CheckCircle2, AlertCircle, Chrome, ChevronRight, Smartphone, X } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

const SECTIONS = {
  speaking_writing: { label: 'Speaking & Writing', desc: 'Read Aloud, Repeat Sentence, Describe Image, Essays and more.' },
  reading: { label: 'Reading', desc: 'Reorder Paragraph, Fill in the Blanks, MCSA, MCMA.' },
  listening: { label: 'Listening', desc: 'Fill in the Blanks, Multiple Choice, Write From Dictation.' },
};

const fmtTime = (s) => {
  const m = Math.floor((s || 0) / 60);
  const sec = Math.max(0, (s || 0) % 60);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Shell + mobile advisory
// ---------------------------------------------------------------------------
const Shell = ({ children }) => (
  <div className="fixed inset-0 z-50 bg-[#0F1720] text-slate-100 overflow-auto" data-testid="pte-sim-shell">
    {children}
  </div>
);

const MobileAdvisory = () => {
  const [show, setShow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 900);
  if (!show) return null;
  return (
    <div className="mx-4 md:mx-auto max-w-3xl mt-4 rounded-xl bg-amber-500/15 border border-amber-500/40 px-4 py-3 flex items-start gap-3"
      data-testid="sim-mobile-advisory">
      <Smartphone className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
      <div className="text-sm text-amber-100 flex-1">
        For the closest PTE simulation experience, we recommend using a desktop or laptop.
        You can still continue on this device.
      </div>
      <button onClick={() => setShow(false)} data-testid="sim-mobile-advisory-dismiss"
        className="text-amber-200 hover:text-white shrink-0 p-1 rounded">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

const Header = ({ section, progress, total, remaining, saveState }) => (
  <div className="border-b border-white/10 bg-black/40 backdrop-blur">
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="text-xs uppercase tracking-widest text-emerald-300 font-bold">Winhouse</div>
        <div className="h-4 w-px bg-white/20" />
        <div className="text-xs uppercase tracking-widest text-slate-300 font-bold">PTE Academic</div>
        {section && <><div className="h-4 w-px bg-white/20" /><div className="text-sm">{section}</div></>}
      </div>
      <div className="flex items-center gap-4 text-sm" data-testid="sim-header-status">
        {typeof progress === 'number' && total > 0 && (
          <div className="font-mono text-slate-300" data-testid="sim-progress">Question {progress + 1} of {total}</div>
        )}
        {typeof remaining === 'number' && (
          <div className={`font-mono px-3 py-1 rounded-lg ${remaining < 60 ? 'bg-red-900/60 text-red-200 animate-pulse' : 'bg-white/10'}`}
            data-testid="sim-timer">{fmtTime(remaining)}</div>
        )}
        {saveState && (
          <div className={`text-xs ${saveState === 'Saved' ? 'text-emerald-300' : 'text-amber-300'}`}
            data-testid="sim-save-state">
            {saveState === 'Saved' ? <span>✓ Saved</span> : saveState}
          </div>
        )}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// System Check
// ---------------------------------------------------------------------------
const CheckRow = ({ icon: Icon, label, state, hint }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
    <Icon className="h-5 w-5 text-slate-300 shrink-0" />
    <div className="flex-1">
      <div className="text-sm">{label}</div>
      {hint && <div className="text-xs text-slate-400">{hint}</div>}
    </div>
    {state === 'ok' ? (
      <span className="inline-flex items-center gap-1 text-emerald-300 text-sm"><CheckCircle2 className="h-4 w-4" />Ready</span>
    ) : state === 'fail' ? (
      <span className="inline-flex items-center gap-1 text-red-300 text-sm"><AlertCircle className="h-4 w-4" />Action Required</span>
    ) : (
      <span className="inline-flex items-center gap-1 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Checking…</span>
    )}
  </div>
);

const SystemCheck = ({ onReady }) => {
  const [browser, setBrowser] = useState('pending');
  const [net, setNet] = useState('pending');
  const [audioOut, setAudioOut] = useState('pending');
  const [micPerm, setMicPerm] = useState('pending');
  const [micRec, setMicRec] = useState('pending');
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    setBrowser(navigator.mediaDevices?.getUserMedia ? 'ok' : 'fail');
    setNet(navigator.onLine ? 'ok' : 'fail');
  }, []);

  const testSpeaker = () => {
    try {
      const u = new SpeechSynthesisUtterance('This is a Winhouse audio test. If you hear this clearly, your speakers are ready.');
      u.onend = () => setAudioOut('ok');
      window.speechSynthesis.speak(u);
    } catch { setAudioOut('fail'); }
  };

  const testMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPerm('ok');
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setPlaybackUrl(URL.createObjectURL(blob));
        setMicRec('ok');
        stream.getTracks().forEach((t) => t.stop());
      };
      recRef.current = rec;
      rec.start();
      setTimeout(() => rec.state === 'recording' && rec.stop(), 3000);
    } catch { setMicPerm('fail'); setMicRec('fail'); }
  };

  const ready = browser === 'ok' && net === 'ok' && audioOut === 'ok' && micPerm === 'ok' && micRec === 'ok';

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-10" data-testid="sim-system-check">
      <div className="text-xs uppercase tracking-widest text-emerald-300 mb-1">System Check</div>
      <h1 className="text-3xl md:text-4xl font-bold mb-2">Let's make sure your device is ready</h1>
      <p className="text-slate-400 mb-6 max-w-xl">Before your PTE Academic practice simulation, we'll check a few things.</p>
      <div className="space-y-3">
        <CheckRow icon={Chrome} label="Browser compatibility" state={browser} hint="Modern browser with audio recording support" />
        <CheckRow icon={Wifi} label="Internet connection" state={net} />
        <CheckRow icon={Volume2} label="Audio output" state={audioOut}
          hint={audioOut === 'pending' ? 'Click Test Speaker to play a sound' : audioOut === 'ok' ? 'Speaker verified' : 'No sound detected'} />
        <CheckRow icon={Mic} label="Microphone permission" state={micPerm} />
        <CheckRow icon={Mic} label="Recording capability" state={micRec}
          hint={micRec === 'ok' ? 'Recorded a 3s sample' : 'A short test recording will be made'} />
      </div>
      <div className="mt-4 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-xs text-slate-300 space-y-1">
        <div>• For the best listening experience, use headphones in a quiet environment.</div>
        <div>• Speak at a normal volume during the microphone test. If your input level appears very low, check your microphone position or input settings.</div>
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        <button onClick={testSpeaker} data-testid="sim-test-speaker"
          className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm">
          Test Speaker
        </button>
        <button onClick={testMic} data-testid="sim-test-mic"
          className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm">
          Test Microphone
        </button>
        {playbackUrl && <audio controls src={playbackUrl} className="max-w-xs" data-testid="sim-mic-playback" />}
      </div>
      <div className="mt-8">
        {ready ? (
          <>
            <div className="mb-3 inline-flex items-center gap-2 text-emerald-300"><CheckCircle2 className="h-5 w-5" />Your system is ready</div>
            <div>
              <button onClick={onReady} data-testid="sim-continue"
                className="px-6 py-3 rounded-full bg-emerald-400 text-black font-bold text-sm inline-flex items-center gap-2">
                Continue <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        ) : (
          <p className="text-slate-400 text-sm">Complete every check above to continue.</p>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Test Info
// ---------------------------------------------------------------------------
const TestInfo = ({ section, onBegin }) => {
  const [accepted, setAccepted] = useState(false);
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-10" data-testid="sim-test-info">
      <div className="text-xs uppercase tracking-widest text-emerald-300 mb-1">PTE Academic</div>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">Practice Simulation · {SECTIONS[section]?.label}</h1>
      <p className="text-slate-400 mb-6 max-w-xl">{SECTIONS[section]?.desc}</p>
      <div className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-3">
        {[
          'Your responses save automatically.',
          'Timed sections continue according to simulation rules.',
          'Speaking tasks require microphone access.',
          'Some audio may play only once, as configured.',
          'Avoid refreshing or closing your browser during the test.',
        ].map((t, i) => (
          <div key={i} className="flex gap-2 items-start text-slate-200 text-sm">
            <span className="text-emerald-300">•</span><span>{t}</span>
          </div>
        ))}
      </div>
      <label className="mt-6 flex items-start gap-2 text-sm cursor-pointer" data-testid="sim-accept-conditions">
        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
        <span>I understand the test conditions.</span>
      </label>
      <div className="mt-6">
        <button onClick={onBegin} disabled={!accepted} data-testid="sim-begin"
          className="px-6 py-3 rounded-full bg-emerald-400 text-black font-bold text-sm inline-flex items-center gap-2 disabled:opacity-40">
          Begin Simulation <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// In-Progress: current question via renderer + timer + save state
// ---------------------------------------------------------------------------
const InProgress = ({ attempt, onComplete }) => {
  const [state, setState] = useState(attempt);
  const [response, setResponse] = useState({});
  const [saveState, setSaveState] = useState('Saved');
  const [connLost, setConnLost] = useState(false);
  const saveTimer = useRef(null);

  // Fetch initial authoritative state immediately (avoids empty player)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await ax.get(`/api/pte/simulation/state/${attempt.id}`);
        if (alive) setState(r.data);
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, [attempt.id]);

  // Poll server for state (authoritative remaining_seconds)
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await ax.get(`/api/pte/simulation/state/${attempt.id}`);
        setState(r.data);
        setConnLost(false);
        if (r.data.remaining_seconds <= 0 && r.data.status === 'in_progress') onComplete(attempt.id);
      } catch (_) {
        setConnLost(true);
      }
    }, 5000);
    return () => clearInterval(id);
  }, [attempt.id, onComplete]);

  const q = state.current_question;
  const idx = state.current_index || 0;
  const total = (state.questions || []).length;
  const Renderer = q && RENDERERS[q.question_type];

  // Debounced autosave
  const save = useCallback(async () => {
    if (!q) return;
    setSaveState('Saving…');
    try {
      await ax.post(`/api/pte/simulation/state/${attempt.id}/save`, {
        question_id: q.id || q.question_id,
        response: response.response ?? null,
        recording_url: response.recording_url || null,
      });
      setSaveState('Saved');
    } catch {
      setSaveState('Reconnecting…');
    }
  }, [attempt.id, q, response]);

  useEffect(() => {
    if (!q) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 900);
    return () => clearTimeout(saveTimer.current);
  }, [response, q, save]);

  // Warn on refresh/close
  useEffect(() => {
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const next = async () => {
    await save();
    setResponse({});
    try {
      const r = await ax.post(`/api/pte/simulation/state/${attempt.id}/advance`);
      if (r.data.completed) onComplete(attempt.id);
      else {
        const s = await ax.get(`/api/pte/simulation/state/${attempt.id}`);
        setState(s.data);
      }
    } catch {
      setSaveState('Reconnecting…');
    }
  };

  if (!q) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
        <div className="text-sm">Preparing your next task…</div>
      </div>
    </div>
  );
  return (
    <div className="min-h-full">
      <Header section={SECTIONS[state.section]?.label} progress={idx} total={total}
        remaining={state.remaining_seconds} saveState={saveState} />
      {connLost && (
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-4">
          <div className="rounded-lg bg-amber-500/15 border border-amber-500/40 px-3 py-2 text-amber-100 text-xs" data-testid="sim-conn-lost">
            We&apos;re reconnecting and saving your response. You can keep working — your progress is safe.
          </div>
        </div>
      )}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        <div className="rounded-2xl bg-white text-slate-900">
          {Renderer ? <Renderer question={q} onResponseChange={setResponse} disabled={false} /> : (
            <div className="p-6 text-slate-500">This task is not yet available in the simulation.</div>
          )}
        </div>
        <div className="mt-6 flex justify-end">
          <button onClick={next} data-testid="sim-next"
            className="px-6 py-2.5 rounded-full bg-emerald-400 text-black font-bold text-sm inline-flex items-center gap-2 hover:bg-emerald-300 transition">
            {idx + 1 >= total ? 'Finish section' : 'Next task'} <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Completion + Results
// ---------------------------------------------------------------------------
const Completion = ({ onView }) => (
  <div className="max-w-2xl mx-auto px-4 md:px-6 py-16 text-center" data-testid="sim-completion">
    <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
    <h1 className="text-3xl md:text-4xl font-bold mb-2">Simulation complete</h1>
    <p className="text-slate-400 mb-6">All responses have been saved successfully.</p>
    <button onClick={onView} data-testid="sim-view-results"
      className="px-6 py-3 rounded-full bg-emerald-400 text-black font-bold text-sm">
      View my results
    </button>
  </div>
);

const Results = ({ results, onDone }) => (
  <div className="max-w-4xl mx-auto px-4 md:px-6 py-10" data-testid="sim-results">
    <div className="rounded-2xl bg-gradient-to-br from-emerald-900 to-slate-900 border border-emerald-800/40 p-8">
      <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2">Winhouse Estimated PTE Practice Score</div>
      <div className="text-6xl md:text-7xl font-black text-white mb-1" data-testid="sim-estimated-score">
        {results.estimated_score ?? '—'}
      </div>
      <div className="text-xs text-slate-400 max-w-xl">{results.estimated_score_disclaimer}</div>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
      {['speaking', 'writing', 'reading', 'listening'].map((s) => {
        const v = results.per_skill?.[s];
        const acc = v?.total ? Math.round((v.correct / v.total) * 100) : null;
        return (
          <div key={s} className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid={`sim-skill-${s}`}>
            <div className="text-xs uppercase tracking-widest text-slate-400">{s}</div>
            <div className="text-2xl font-bold mt-1">{acc !== null ? `${acc}%` : '—'}</div>
            <div className="text-xs text-slate-500">{v?.total ? `${v.correct}/${v.total} objective` : 'No objective tasks'}{v?.review ? ` · ${v.review} awaiting review` : ''}</div>
          </div>
        );
      })}
    </div>

    <div className="grid md:grid-cols-2 gap-3 mt-5">
      <div className="rounded-xl bg-white/5 border border-white/10 p-5">
        <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2">Strongest skill</div>
        <div className="text-lg font-semibold capitalize">{results.strongest_skill || '—'}</div>
      </div>
      <div className="rounded-xl bg-white/5 border border-white/10 p-5">
        <div className="text-xs uppercase tracking-widest text-amber-300 mb-2">Focus area</div>
        <div className="text-lg font-semibold capitalize">{results.weakest_skill || '—'}</div>
      </div>
    </div>

    {results.review_pending > 0 && (
      <div className="rounded-xl bg-amber-900/40 border border-amber-600/40 p-4 mt-5 text-amber-100 text-sm" data-testid="sim-review-pending">
        <strong>{results.review_pending}</strong> {results.review_pending === 1 ? 'response is' : 'responses are'} awaiting tutor review. Objective performance above is final; speaking/writing components will appear when your tutor completes the review.
      </div>
    )}

    {(results.insights || []).length > 0 && (
      <div className="rounded-xl bg-white/5 border border-white/10 p-5 mt-5" data-testid="sim-insights">
        <div className="text-xs uppercase tracking-widest text-emerald-300 mb-3">Insights</div>
        <ul className="space-y-1 text-sm">
          {results.insights.map((i, k) => <li key={k}>• {i.message}</li>)}
        </ul>
      </div>
    )}

    <div className="mt-6 flex justify-between text-xs text-slate-500 flex-wrap gap-2">
      <div>Duration: {fmtTime(results.duration_seconds)}</div>
      <div>Tasks completed: {results.task_count}</div>
    </div>

    <div className="mt-6">
      <button onClick={onDone} data-testid="sim-return"
        className="px-6 py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm">
        Return to PTE hub
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main page — state machine
// ---------------------------------------------------------------------------
export default function PTESimulationPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get('view');           // 'results' | 'resume' | null
  const attemptParam = searchParams.get('attempt');
  const [phase, setPhase] = useState('system_check');   // system_check | test_info | in_progress | complete | results
  const [attempt, setAttempt] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(!!viewParam);

  // Deep-link bootstrap: view=results&attempt=<id> jumps straight to results;
  // view=resume picks up the latest in_progress attempt from the server.
  useEffect(() => {
    let alive = true;
    if (!viewParam) return;
    (async () => {
      try {
        if (viewParam === 'results' && attemptParam) {
          const r = await ax.get(`/api/pte/simulation/state/${attemptParam}`);
          if (!alive) return;
          if (r.data.status === 'completed' && r.data.results) {
            setResults(r.data.results);
            setPhase('results');
          } else {
            setAttempt(r.data);
            setPhase('in_progress');
          }
        } else if (viewParam === 'resume') {
          const r = await ax.get('/api/pte/simulation/resume');
          if (!alive) return;
          if (r.data.resumable && r.data.attempt) {
            setAttempt(r.data.attempt);
            setPhase('in_progress');
          } else {
            setPhase('system_check');
          }
        }
      } catch (_) {
        if (alive) setPhase('system_check');
      } finally {
        if (alive) setBootstrapping(false);
      }
    })();
    return () => { alive = false; };
  }, [viewParam, attemptParam]);

  const begin = useCallback(async () => {
    try {
      const r = await ax.post('/api/pte/simulation/start', { section, mode: 'simulation' });
      setAttempt(r.data);
      setPhase('in_progress');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setError('Please sign in as a student to start a PTE simulation.');
      } else if (status === 404) {
        setError('There are no simulation-eligible questions in this section yet. Please try another section.');
      } else {
        setError('We could not start your simulation right now. Please try again in a moment.');
      }
    }
  }, [section]);

  const complete = useCallback(async (id) => {
    try {
      const r = await ax.post(`/api/pte/simulation/state/${id}/complete`);
      setResults(r.data.results || r.data);
      setPhase('complete');
    } catch { setPhase('complete'); }
  }, []);

  if (!SECTIONS[section]) {
    return (
      <Shell>
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
          <div className="text-slate-200 mb-4">This section isn&apos;t available for simulation yet.</div>
          <button onClick={() => navigate('/resources/pte-academic')}
            className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm">Back to PTE hub</button>
        </div>
      </Shell>
    );
  }
  if (error) {
    return (
      <Shell>
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
          <div className="text-slate-200 mb-4" data-testid="sim-error-msg">{error}</div>
          <div className="flex justify-center gap-3">
            <button onClick={() => navigate('/student/login')}
              className="px-4 py-2 rounded-full bg-emerald-400 text-black text-sm font-bold">Sign in</button>
            <button onClick={() => navigate('/resources/pte-academic')}
              className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm">Back</button>
          </div>
        </div>
      </Shell>
    );
  }
  if (bootstrapping) {
    return (
      <Shell>
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-3 text-slate-400 pt-40">
            <Loader2 className="h-6 w-6 animate-spin" />
            <div className="text-sm">Loading your simulation…</div>
          </div>
        </div>
      </Shell>
    );
  }
  return (
    <Shell>
      {(phase === 'system_check' || phase === 'test_info') && <MobileAdvisory />}
      {phase === 'system_check' && <SystemCheck onReady={() => setPhase('test_info')} />}
      {phase === 'test_info' && <TestInfo section={section} onBegin={begin} />}
      {phase === 'in_progress' && attempt && <InProgress attempt={attempt} onComplete={complete} />}
      {phase === 'complete' && <Completion onView={() => setPhase('results')} />}
      {phase === 'results' && results && (
        <Results results={results} onDone={() => navigate('/student/dashboard')} />
      )}
    </Shell>
  );
}
